'use client';

import { DEFAULT_DOMAIN } from '@/lib/constants';
import {
  generateBreadcrumbStructuredData,
  validateBreadcrumbItems,
} from '@/lib/structured-data/generators';
import { StructuredData } from '@/lib/structured-data/StructuredData';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import styles from './breadcrumb.module.scss';
import { getExperiencePreviewCrumbs, type ExperiencePreviewCrumb } from './getExperienceParent';

export type BreadcrumbItemData = {
  label: string;
  href: string;
};

export interface BreadcrumbProps {
  items?: BreadcrumbItemData[];
  enableStructuredData?: boolean;
  baseUrl?: string;
}

function buildCrumbsFromPathname(pathname: string): BreadcrumbItemData[] {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return [];

  return [
    { label: 'Home', href: '/' },
    ...segments.map((segment, idx) => {
      const href = '/' + segments.slice(0, idx + 1).join('/');
      const raw = decodeURIComponent(segment).replace(/-/g, ' ');
      const lc = raw.toLowerCase();
      const label = lc
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return { label, href };
    }),
  ];
}

/**
 * On the /experience preview route, fetch the experience's parent page
 * and insert it between Home and the current page crumb, replacing the
 * current page label with the real experience title.
 */
function usePreviewParentCrumb(
  pathname: string,
  crumbs: BreadcrumbItemData[],
): BreadcrumbItemData[] {
  const searchParams = useSearchParams();
  const slug = searchParams.get('slug');
  const [previewCrumbs, setPreviewCrumbs] = useState<ExperiencePreviewCrumb[] | null>(null);

  const isPreviewRoute = pathname === '/experience';

  useEffect(() => {
    if (!isPreviewRoute || !slug) return;

    getExperiencePreviewCrumbs(slug).then((result) => {
      if (result) setPreviewCrumbs(result);
    });
  }, [isPreviewRoute, slug]);

  if (!isPreviewRoute || !previewCrumbs) return crumbs;

  return previewCrumbs;
}

export const Breadcrumb = ({
  items,
  enableStructuredData = true,
  baseUrl = DEFAULT_DOMAIN,
}: BreadcrumbProps = {}) => {
  const pathname = usePathname();
  const baseCrumbs =
    !pathname || pathname === '/'
      ? []
      : items && items.length > 0
        ? items
        : buildCrumbsFromPathname(pathname);
  const crumbs = usePreviewParentCrumb(pathname || '', baseCrumbs);

  if (!pathname || pathname === '/') return null;

  if (crumbs.length === 0) return null;

  const shouldIncludeStructuredData = enableStructuredData && validateBreadcrumbItems(crumbs);
  const breadcrumbStructuredData = shouldIncludeStructuredData
    ? generateBreadcrumbStructuredData(crumbs, baseUrl)
    : null;

  return (
    <>
      {breadcrumbStructuredData && (
        <StructuredData
          data={breadcrumbStructuredData}
          id={`breadcrumb-structured-data-${pathname.replace(/\//g, '-')}`}
        />
      )}
      <nav aria-label="breadcrumb" className={cn(styles.breadcrumbWrapper, styles.exp)}>
        <ol className={styles.breadcrumb}>
          {crumbs.map((crumb, idx) => {
            const isLast = idx === crumbs.length - 1;
            return (
              <li key={crumb.href} className="flex items-center">
                {isLast ? (
                  <span className={cn(styles.breadcrumb__link, styles['breadcrumb__link--active'])}>
                    {crumb.label}
                  </span>
                ) : (
                  <Link href={crumb.href} className={styles.breadcrumb__link}>
                    {crumb.label}
                  </Link>
                )}

                {!isLast && (
                  <ChevronRight
                    size={16}
                    strokeWidth={2}
                    className={styles.breadcrumb__separator}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};
